import { resolveLocalizedText } from "../../../utils/questionUtils";
import { browserLanguageIsSpanish } from "../../../utils/locale";
import { WorkflowDefinition, WorkflowName } from "../../../apis/API";

/** A label available in the two supported languages. */
export interface BilingualLabel {
  en: string;
  es: string;
}

/**
 * Client-side default workflow label catalog (English + Spanish), ported from
 * the Android `res/values/question_forms.xml` + `res/values-es/...`. Used as the
 * fallback when the server `WorkflowDefinition` does not supply a label. Keys
 * follow the Android `qf_<section>_<workflow>_<step>_<key>` scheme.
 *
 * Until the participant webapp has a general i18n mechanism (IHA-370), this is
 * the bilingual source for workflow labels.
 */
export const WORKFLOW_LABELS: Record<string, BilingualLabel> = {
  qf_common_yes: { en: "Yes", es: "Sí" },
  qf_common_no: { en: "No", es: "No" },
  qf_common_add_details: {
    en: "Add details.",
    es: "Agregue detalles.",
  },
  qf_behavioral_explain_below: {
    en: "Explain.",
    es: "Explique.",
  },
  qf_treatment_list_title: {
    en: "List treatment programs.",
    es: "Liste los programas de tratamiento.",
  },
  qf_treatment_add_program: {
    en: "Add another program",
    es: "Agregar otro programa",
  },
  // Singular per-entry label shared by the five treatment/program lists (the
  // "#N" index is appended by the component).
  qf_treatment_list_program: {
    en: "Treatment Program",
    es: "Programa de tratamiento",
  },
  qf_residence_addressConfirmation_root_addressOnFile: {
    en: "Address on file",
    es: "Dirección en archivo",
  },
  qf_residence_addressConfirmation_root_lastConfirmed: {
    en: "Last confirmed",
    es: "Última confirmación",
  },
  qf_residence_addressConfirmation_root_correctBtn: {
    en: "This address is correct",
    es: "Esta dirección es correcta",
  },
  qf_residence_addressConfirmation_root_changedBtn: {
    en: "My address has changed",
    es: "Mi dirección ha cambiado",
  },
  qf_residence_addressConfirmation_entry_title: {
    en: "Enter your current address.",
    es: "Ingrese su dirección actual.",
  },
  qf_residence_addressConfirmation_entry_streetAddress: {
    en: "Street Address",
    es: "Dirección",
  },
  qf_residence_addressConfirmation_entry_city: { en: "City", es: "Ciudad" },
  qf_residence_addressConfirmation_entry_state: { en: "State", es: "Estado" },
  qf_residence_addressConfirmation_entry_zipCode: {
    en: "Zip Code",
    es: "Código postal",
  },
  qf_phone_phoneConfirmation_root_lastReportedPhone: {
    en: "Last Reported Phone Number",
    es: "Último número de teléfono reportado",
  },
  // Keyed by the field key (mobile/other) so a card lookup by field key resolves;
  // displayed as "Main"/"Other" per Figma.
  qf_phone_phoneConfirmation_root_mobile: { en: "Main", es: "Principal" },
  qf_phone_phoneConfirmation_root_other: { en: "Other", es: "Otro" },
  qf_phone_phoneConfirmation_root_correctBtn: {
    en: "These numbers are correct",
    es: "Estos números son correctos",
  },
  qf_phone_phoneConfirmation_root_changedBtn: {
    en: "These numbers have changed",
    es: "Estos números han cambiado",
  },
  // Ask-flow root (no phone number on file) — the confirm-flow headline is the
  // server prompt ("Has your phone number changed…?"); see EHA-861/IHA-407.
  qf_phone_phoneConfirmation_root_askRoot: {
    en: "Do you have any phone numbers?",
    es: "¿Tiene algún número de teléfono?",
  },
  qf_phone_phoneConfirmation_entry_title: {
    en: "Enter your current phone numbers.",
    es: "Ingrese sus números de teléfono actuales.",
  },
  qf_phone_phoneConfirmation_entry_mobile: { en: "Main", es: "Principal" },
  qf_phone_phoneConfirmation_entry_other: { en: "Other", es: "Otro" },
  qf_getStarted_appointment_root_scheduledAppt: {
    en: "Scheduled Appointment",
    es: "Cita programada",
  },
  qf_getStarted_appointment_root_dateLabel: { en: "Date", es: "Fecha" },
  qf_getStarted_appointment_root_timeLabel: { en: "Time", es: "Hora" },
  qf_employmentSchool_employment_entry_title: {
    en: "Enter your employer details.",
    es: "Ingrese los detalles de su empleador.",
  },
  // "Have there been changes?" confirm step shown above the on-file jobs when
  // the participant has jobs on file (EmploymentForm changes step).
  qf_employmentSchool_employment_changes_title: {
    en: "Have there been changes to your jobs?",
    es: "¿Ha habido cambios en sus empleos?",
  },
  qf_employmentSchool_employment_entry_employerName: {
    en: "Employer Name",
    es: "Nombre del empleador",
  },
  qf_employmentSchool_employment_entry_jobTitle: {
    en: "Job Title",
    es: "Puesto de trabajo",
  },
  qf_employmentSchool_employment_entry_supervisorName: {
    en: "Supervisor Name",
    es: "Nombre del supervisor",
  },
  qf_employmentSchool_employment_entry_streetAddress: {
    en: "Street Address",
    es: "Dirección",
  },
  qf_employmentSchool_employment_entry_city: { en: "City", es: "Ciudad" },
  qf_employmentSchool_employment_entry_state: { en: "State", es: "Estado" },
  qf_employmentSchool_employment_entry_zipCode: {
    en: "Zip Code",
    es: "Código postal",
  },
  qf_employmentSchool_employment_entry_addMore: {
    en: "Add another job",
    es: "Agregar otro empleo",
  },
  // Per-entry header noun (repeatable employment): rendered as "Job #1", etc.
  qf_employmentSchool_employment_entry_itemLabel: {
    en: "Job",
    es: "Empleo",
  },
  qf_drivingVehicles_vehicle_entry_title: {
    en: "Enter your vehicle details.",
    es: "Ingrese los detalles de su vehículo.",
  },
  qf_drivingVehicles_vehicle_entry_year: { en: "Year", es: "Año" },
  qf_drivingVehicles_vehicle_entry_make: { en: "Make", es: "Marca" },
  qf_drivingVehicles_vehicle_entry_model: { en: "Model", es: "Modelo" },
  qf_drivingVehicles_vehicle_entry_color: { en: "Color", es: "Color" },
  qf_drivingVehicles_vehicle_entry_plateNum: {
    en: "License Plate #",
    es: "Número de placa",
  },
  qf_drivingVehicles_vehicle_entry_ownerName: {
    en: "Owner's Name",
    es: "Nombre del propietario",
  },
  qf_drivingVehicles_vehicle_entry_insurance: {
    en: "Insurance Provider",
    es: "Proveedor de seguro",
  },
  qf_drivingVehicles_vehicle_entry_addMore: {
    en: "Add another vehicle",
    es: "Agregar otro vehículo",
  },
  // Per-entry header noun (repeatable vehicles): rendered as "Vehicle #1", etc.
  qf_drivingVehicles_vehicle_entry_itemLabel: {
    en: "Vehicle",
    es: "Vehículo",
  },
  qf_travel_travel_entry_title: {
    en: "Please provide the date, location visited and reason.",
    es: "Por favor proporcione la fecha, el lugar visitado y el motivo.",
  },
  qf_travel_travel_entry_date: { en: "Date", es: "Fecha" },
  qf_travel_travel_entry_location: { en: "Location", es: "Ubicación" },
  qf_travel_travel_entry_reason: { en: "Reason", es: "Motivo" },
  qf_contact_emergencyContact_entry_title: {
    en: "Provide their name and phone number.",
    es: "Proporcione su nombre y número de teléfono.",
  },
  // Suffix tracks the registry fieldKey (`name`/`phone`) — resolveConfirmOnFileLabel
  // derives the lookup key as qf_<section>_<workflow>_<stepId>_<fieldKey>. The
  // `entry` labels double as the confirm-flow on-file card labels (name/phone).
  qf_contact_emergencyContact_entry_name: { en: "Name", es: "Nombre" },
  qf_contact_emergencyContact_entry_phone: {
    en: "Phone Number",
    es: "Número de teléfono",
  },
  // Confirm-or-collect root labels (EHA-861/IHA-407). Confirm-flow headline is
  // the server prompt ("Have there been changes…?"); these cover the confirm
  // buttons + on-file header and the ask-flow root ("Do you have…?").
  qf_contact_emergencyContact_root_correctBtn: {
    en: "This information is correct",
    es: "Esta información es correcta",
  },
  qf_contact_emergencyContact_root_changedBtn: {
    en: "This information has changed",
    es: "Esta información ha cambiado",
  },
  qf_contact_emergencyContact_root_emergencyContactOnFile: {
    en: "Emergency contact on file",
    es: "Contacto de emergencia registrado",
  },
  qf_contact_emergencyContact_root_askRoot: {
    en: "Do you have an emergency contact?",
    es: "¿Tiene un contacto de emergencia?",
  },
  qf_financialCourt_feesPayment_entry_title: {
    en: "Enter payment details.",
    es: "Ingrese los detalles del pago.",
  },
  qf_financialCourt_feesPayment_entry_amount: { en: "Amount", es: "Monto" },
  qf_financialCourt_feesPayment_entry_date: { en: "Date", es: "Fecha" },
  qf_medications_medications_entry_title: {
    en: "Please list all prescribed medications.",
    es: "Por favor liste todos los medicamentos recetados.",
  },
  // Singular per-entry label (each input holds one item; unnumbered per design).
  qf_medications_medications_entry_medicationLabel: {
    en: "Medication",
    es: "Medicamento",
  },
  qf_medications_medications_entry_addMore: {
    en: "Add another medication",
    es: "Agregar otro medicamento",
  },
  qf_employmentSchool_school_entry_title: {
    en: "Enter your school or training program details.",
    es: "Ingrese los detalles de su escuela o programa de capacitación.",
  },
  qf_employmentSchool_school_entry_programName: {
    en: "Educational Program",
    es: "Programa educativo",
  },
  qf_employmentSchool_school_entry_addMore: {
    en: "Add another program",
    es: "Agregar otro programa",
  },
  qf_contact_email_entry_title: {
    en: "Enter your email address.",
    es: "Ingrese su dirección de correo electrónico.",
  },
  // Field label shared by the on-file card row and the entry input.
  qf_contact_email_entry_email: {
    en: "Email",
    es: "Correo electrónico",
  },
  // Confirm branch (an on-file email exists): "Has it changed?" root, shown
  // instead of the plain Yes/No collect root (IHA-395/EHA-908).
  qf_contact_email_rootEmailPresent_hasEmailChanged: {
    en: "Has your email address changed since you last reported?",
    es: "¿Ha cambiado su dirección de correo electrónico desde la última vez que informó?",
  },
  qf_contact_email_rootEmailPresent_emailAddressOnFile: {
    en: "Email address on file",
    es: "Dirección de correo electrónico registrada",
  },
  qf_contact_email_rootEmailPresent_correctBtn: {
    en: "This email address is correct",
    es: "Esta dirección de correo electrónico es correcta",
  },
  qf_contact_email_rootEmailPresent_changedBtn: {
    en: "The email address has changed",
    es: "La dirección de correo electrónico ha cambiado.",
  },
  qf_residence_householdMembers_root_addMore: {
    en: "Add another member",
    es: "Agregar otro miembro",
  },
  qf_residence_householdMembers_root_householdMemberLabel: {
    en: "Household Member",
    es: "Miembro del hogar",
  },
  qf_residence_householdMembers_root_skipBtn: {
    en: "I live alone - skip",
    es: "Vivo solo - omitir",
  },
  qf_general_equipmentIssues_entry_title: {
    en: "Please explain equipment issues you are having.",
    es: "Por favor explique los problemas de equipo que está teniendo.",
  },
  qf_residence_planAddressChange_entry_title: {
    en: "Please explain why you are changing your address and your plans for moving.",
    es: "Por favor explique por qué está cambiando su dirección y sus planes de mudanza.",
  },
  qf_residence_householdChange_entry_title: {
    en: "Please explain why the people in your household are changing.",
    es: "Por favor explique por qué las personas en su hogar están cambiando.",
  },
  qf_phone_planPhoneChange_entry_title: {
    en: "Please explain why your phone number is changing.",
    es: "Por favor explique por qué su número de teléfono está cambiando.",
  },
  qf_employmentSchool_employmentIssues_entry_title: {
    en: "Please explain your employment issues.",
    es: "Por favor explique sus problemas de empleo.",
  },
  qf_employmentSchool_schoolIssues_entry_title: {
    en: "Please explain your school issues.",
    es: "Por favor explique sus problemas en la escuela.",
  },
  qf_legalLawEnforcement_lawEnforcement_entry_title: {
    en: "Please provide date, time, agency and reason.",
    es: "Por favor proporcione fecha, hora, agencia y motivo.",
  },
  qf_officerFollowup_problemsDiscuss_entry_title: {
    en: "Please explain what you would like to discuss with your officer.",
    es: "Por favor explique lo que le gustaría discutir con su oficial.",
  },
  qf_officerFollowup_officerReporting_entry_title: {
    en: "Enter the officer's name, court case number, and reason for reporting.",
    es: "Ingrese el nombre del oficial, el número de caso judicial y el motivo del reporte.",
  },
  qf_officerFollowup_officerReporting_entry_officerName: {
    en: "Officer Name",
    es: "Nombre del oficial",
  },
  qf_officerFollowup_officerReporting_entry_courtCase: {
    en: "Court Case #",
    es: "Número de caso judicial",
  },
  qf_officerFollowup_officerReporting_entry_reason: {
    en: "Reason",
    es: "Motivo",
  },
  qf_submission_falseClaims_entry_title: {
    en: "Please explain any false claims, omissions or untrue statements made.",
    es: "Por favor explique cualquier declaración falsa, omisión o afirmación incorrecta realizada.",
  },
  qf_financialCourt_childSupport_entry_title: {
    en: "List amount owed and the monthly payment.",
    es: "Liste el monto adeudado y el pago mensual.",
  },
  qf_financialCourt_feesRestitution_entry_title: {
    en: "List the fees owed and amounts paid towards any listed.",
    es: "Liste las tarifas adeudadas y los montos pagados hacia las listadas.",
  },
};

/** Currently supported catalog languages. */
type CatalogLanguage = keyof BilingualLabel;

/**
 * Pick the catalog language for the participant: Spanish when the device locale
 * is Spanish (`es*`), English otherwise (default + fallback). Mirrors the
 * exact→same-language→English preference of `resolveLocalizedText` for the
 * two-language catalog.
 */
const catalogLanguage = (): CatalogLanguage =>
  browserLanguageIsSpanish() ? "es" : "en";

/** Bilingual suffix appended to a required field's label (IHA-388). */
const REQUIRED_SUFFIX: BilingualLabel = {
  en: " (Required)",
  es: " (Obligatorio)",
};

/**
 * Append the localized "(Required)" marker to a field label when the field is
 * required; otherwise return the label unchanged. The visible required cue used
 * across the workflow forms (Figma marks required fields with `*`; product
 * chose the word instead). Language follows `catalogLanguage()`.
 */
export const withRequiredSuffix = (label: string, required: boolean): string =>
  required ? `${label}${REQUIRED_SUFFIX[catalogLanguage()]}` : label;

/**
 * Resolve a server-supplied label from a `WorkflowDefinition`: find the step by
 * `stepId`, the label by `labelKey`, then pick the locale-appropriate text.
 * Returns `undefined` when absent/blank. (Free function — `WorkflowDefinition`
 * is plain data, not a class.)
 */
export const findLabel = (
  workflowDefinition: WorkflowDefinition | null | undefined,
  stepId: string,
  labelKey: string,
): string | undefined => {
  const step = workflowDefinition?.steps?.find((s) => s?.stepId === stepId);
  const label = step?.labels?.find((l) => l?.key === labelKey);
  const text = resolveLocalizedText(label?.value);
  return text && text.length > 0 ? text : undefined;
};

/**
 * Resolve a workflow label: prefer the server `WorkflowDefinition` value, then
 * fall back to the client catalog default for the active language. Returns
 * `undefined` if neither has it, so the caller can apply its own fallback.
 */
export const resolveWorkflowLabel = (params: {
  workflowDefinition?: WorkflowDefinition | null;
  stepId: string;
  labelKey: string;
  /** Catalog key (`qf_...`) for the client default. */
  catalogKey?: string;
}): string | undefined => {
  const serverLabel = findLabel(
    params.workflowDefinition,
    params.stepId,
    params.labelKey,
  );
  if (serverLabel) {
    return serverLabel;
  }
  const fallback = params.catalogKey
    ? WORKFLOW_LABELS[params.catalogKey]
    : undefined;
  return fallback ? fallback[catalogLanguage()] : undefined;
};

/**
 * Client-default entry-step *title* (the "Provide Details" heading) per
 * YES_NO_FREE_TEXT workflow. The 20 sex-offender behavioral workflows share
 * "Explain below."; the rest carry workflow-specific prompts. Used as the
 * fallback when the server `WorkflowDefinition` omits the entry title.
 */
const SO_BEHAVIORAL_TITLE = "qf_behavioral_explain_below";
const FREE_TEXT_ENTRY_TITLE_KEY: Partial<Record<WorkflowName, string>> = {
  planAddressChange: "qf_residence_planAddressChange_entry_title",
  householdChange: "qf_residence_householdChange_entry_title",
  planPhoneChange: "qf_phone_planPhoneChange_entry_title",
  employmentIssues: "qf_employmentSchool_employmentIssues_entry_title",
  schoolIssues: "qf_employmentSchool_schoolIssues_entry_title",
  lawEnforcement: "qf_legalLawEnforcement_lawEnforcement_entry_title",
  problemsDiscuss: "qf_officerFollowup_problemsDiscuss_entry_title",
  falseClaims: "qf_submission_falseClaims_entry_title",
  childSupport: "qf_financialCourt_childSupport_entry_title",
  feesRestitution: "qf_financialCourt_feesRestitution_entry_title",
  equipmentIssues: "qf_general_equipmentIssues_entry_title",
  soChildContact: SO_BEHAVIORAL_TITLE,
  soAngerFemales: SO_BEHAVIORAL_TITLE,
  soViolent: SO_BEHAVIORAL_TITLE,
  soAttracted: SO_BEHAVIORAL_TITLE,
  soFollowing: SO_BEHAVIORAL_TITLE,
  soMasturbated: SO_BEHAVIORAL_TITLE,
  soDeviantFantasies: SO_BEHAVIORAL_TITLE,
  soSexualContact: SO_BEHAVIORAL_TITLE,
  soPornography: SO_BEHAVIORAL_TITLE,
  soInternet: SO_BEHAVIORAL_TITLE,
  soViolatedSupervision: SO_BEHAVIORAL_TITLE,
  soSubstances: SO_BEHAVIORAL_TITLE,
  soAssociatedUsers: SO_BEHAVIORAL_TITLE,
  soEmotions: SO_BEHAVIORAL_TITLE,
  soHurting: SO_BEHAVIORAL_TITLE,
  soOutOfControl: SO_BEHAVIORAL_TITLE,
  soLostRelationships: SO_BEHAVIORAL_TITLE,
  soNewFriends: SO_BEHAVIORAL_TITLE,
  soSafetyNet: SO_BEHAVIORAL_TITLE,
  soChurch: SO_BEHAVIORAL_TITLE,
};

/**
 * The "Provide Details" entry heading for a YES_NO_FREE_TEXT workflow: prefer the
 * server `WorkflowDefinition` entry title, then the client catalog default.
 */
export const resolveFreeTextEntryTitle = (
  workflowName: WorkflowName | null | undefined,
  workflowDefinition: WorkflowDefinition | null | undefined,
): string | undefined =>
  resolveWorkflowLabel({
    workflowDefinition,
    stepId: "entry",
    labelKey: "title",
    catalogKey: workflowName
      ? FREE_TEXT_ENTRY_TITLE_KEY[workflowName]
      : undefined,
  });

/**
 * Catalog-key prefix per CONFIRM_ON_FILE workflow (the section differs:
 * residence / phone / getStarted). Used to build `qf_<section>_<workflow>_<step>_<key>`.
 */
const CONFIRM_ON_FILE_PREFIX: Partial<Record<WorkflowName, string>> = {
  addressConfirmation: "qf_residence_addressConfirmation",
  phoneConfirmation: "qf_phone_phoneConfirmation",
  appointment: "qf_getStarted_appointment",
  email: "qf_contact_email",
  emergencyContact: "qf_contact_emergencyContact",
};

/**
 * Resolve a CONFIRM_ON_FILE label: server `WorkflowDefinition` first, then the
 * client catalog default at `qf_<section>_<workflow>_<stepId>_<labelKey>`.
 */
export const resolveConfirmOnFileLabel = (
  workflowName: WorkflowName | null | undefined,
  workflowDefinition: WorkflowDefinition | null | undefined,
  stepId: string,
  labelKey: string,
): string | undefined => {
  const prefix = workflowName
    ? CONFIRM_ON_FILE_PREFIX[workflowName]
    : undefined;
  return resolveWorkflowLabel({
    workflowDefinition,
    stepId,
    labelKey,
    catalogKey: prefix ? `${prefix}_${stepId}_${labelKey}` : undefined,
  });
};

/**
 * Catalog-key prefix per YES_NO_STRUCTURED_FORM workflow. Sections are irregular
 * (employmentSchool / drivingVehicles / travel / contact / financialCourt /
 * officerFollowup), and the `vehicles` workflow name maps to the singular
 * `vehicle` catalog segment. Used to build `qf_<section>_<workflow>_<step>_<key>`.
 */
const STRUCTURED_FORM_PREFIX: Partial<Record<WorkflowName, string>> = {
  employment: "qf_employmentSchool_employment",
  vehicles: "qf_drivingVehicles_vehicle",
  travel: "qf_travel_travel",
  feesPayment: "qf_financialCourt_feesPayment",
  officerReporting: "qf_officerFollowup_officerReporting",
};

/**
 * Resolve a YES_NO_STRUCTURED_FORM label: server `WorkflowDefinition` first, then
 * the client catalog default at `qf_<section>_<workflow>_<stepId>_<labelKey>`.
 */
export const resolveStructuredFormLabel = (
  workflowName: WorkflowName | null | undefined,
  workflowDefinition: WorkflowDefinition | null | undefined,
  stepId: string,
  labelKey: string,
): string | undefined => {
  const prefix = workflowName
    ? STRUCTURED_FORM_PREFIX[workflowName]
    : undefined;
  return resolveWorkflowLabel({
    workflowDefinition,
    stepId,
    labelKey,
    catalogKey: prefix ? `${prefix}_${stepId}_${labelKey}` : undefined,
  });
};

/**
 * Per-workflow label config for the repeatable-list templates
 * (YES_NO_REPEATABLE_LIST + DIRECT_LIST). The list catalog keys are irregular:
 * medications/school use `qf_<section>_<workflow>_entry_*`; the five
 * treatment/program workflows share `qf_treatment_list_*`; householdMembers uses
 * stepId `root` (and carries the save/skip button labels).
 */
interface RepeatableListLabelConfig {
  stepId: string;
  /** Field key (= server field-label key) for the per-entry input label. */
  fieldKey: string;
  /** Catalog key for the per-entry input label (a singular, unnumbered noun). */
  fieldCatalogKey: string;
  titleKey?: string;
  addMoreKey: string;
  skipKey?: string;
}

// The five treatment/program workflows are structurally identical (shared spec +
// shared catalog keys); only the server-supplied question text differs.
const TREATMENT_LIST_LABELS: RepeatableListLabelConfig = {
  stepId: "entry",
  fieldKey: "program",
  fieldCatalogKey: "qf_treatment_list_program",
  titleKey: "qf_treatment_list_title",
  addMoreKey: "qf_treatment_add_program",
};

const REPEATABLE_LIST_CONFIG: Partial<
  Record<WorkflowName, RepeatableListLabelConfig>
> = {
  medications: {
    stepId: "entry",
    fieldKey: "medicationLabel",
    fieldCatalogKey: "qf_medications_medications_entry_medicationLabel",
    titleKey: "qf_medications_medications_entry_title",
    addMoreKey: "qf_medications_medications_entry_addMore",
  },
  school: {
    stepId: "entry",
    fieldKey: "programName",
    fieldCatalogKey: "qf_employmentSchool_school_entry_programName",
    titleKey: "qf_employmentSchool_school_entry_title",
    addMoreKey: "qf_employmentSchool_school_entry_addMore",
  },
  // `email` is CONFIRM_ON_FILE (confirm-or-collect), not a repeatable list —
  // see CONFIRM_ON_FILE_PREFIX / resolveConfirmOnFileLabel (IHA-395/EHA-908).
  treatmentPrograms: TREATMENT_LIST_LABELS,
  naAa: TREATMENT_LIST_LABELS,
  mentalHealthTreatment: TREATMENT_LIST_LABELS,
  sexOffenderTreatment: TREATMENT_LIST_LABELS,
  communityService: TREATMENT_LIST_LABELS,
  householdMembers: {
    stepId: "root",
    fieldKey: "householdMemberLabel",
    fieldCatalogKey: "qf_residence_householdMembers_root_householdMemberLabel",
    addMoreKey: "qf_residence_householdMembers_root_addMore",
    skipKey: "qf_residence_householdMembers_root_skipBtn",
  },
};

/** Which repeatable-list label to resolve. */
export type RepeatableListSemantic =
  | "title"
  | "field"
  | "addMore"
  | "skip";

/**
 * Resolve a repeatable-list label (server `WorkflowDefinition` first, then the
 * client catalog). Returns `undefined` for an unmapped workflow or an absent
 * server+catalog label, so callers can fall back.
 */
export const resolveRepeatableListLabel = (
  workflowName: WorkflowName | null | undefined,
  workflowDefinition: WorkflowDefinition | null | undefined,
  semantic: RepeatableListSemantic,
): string | undefined => {
  const cfg = workflowName ? REPEATABLE_LIST_CONFIG[workflowName] : undefined;
  if (!cfg) {
    return undefined;
  }
  switch (semantic) {
    case "field":
      return resolveWorkflowLabel({
        workflowDefinition,
        stepId: cfg.stepId,
        labelKey: cfg.fieldKey,
        catalogKey: cfg.fieldCatalogKey,
      });
    case "title":
      return resolveWorkflowLabel({
        workflowDefinition,
        stepId: cfg.stepId,
        labelKey: "title",
        catalogKey: cfg.titleKey,
      });
    case "addMore":
      return resolveWorkflowLabel({
        workflowDefinition,
        stepId: cfg.stepId,
        labelKey: "addMore",
        catalogKey: cfg.addMoreKey,
      });
    case "skip":
      return resolveWorkflowLabel({
        workflowDefinition,
        stepId: cfg.stepId,
        labelKey: "skipBtn",
        catalogKey: cfg.skipKey,
      });
  }
};

/**
 * The detail textarea label ("Add details."): server `WorkflowDefinition`
 * first, then the shared `qf_common_add_details` catalog default.
 */
export const resolveAddDetailsLabel = (
  workflowDefinition: WorkflowDefinition | null | undefined,
): string | undefined =>
  resolveWorkflowLabel({
    workflowDefinition,
    stepId: "entry",
    labelKey: "addDetailsLabel",
    catalogKey: "qf_common_add_details",
  });
